import React, { useRef, useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { setSEO } from '../utils/seo';
import './About.css';

// SEO
const _aboutSEO = setSEO;
function useReveal() {
  const ref = useRef(null);
  const [vis, setVis] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) { setVis(true); obs.disconnect(); } }, { threshold: 0.1 });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);
  return [ref, vis];
}

export default function About() {
  useEffect(() => {
    setSEO({ title: "About Us – MS Discoveries | Chatbot CRM, Email Marketing & SEO India", description: "Learn about MS Discoveries – India-based digital growth company offering Chatbot CRM, Email Marketing, Email Verification, SEO, AEO, Web Development, iOS & Android Apps and Data Services.", canonical: "/about" });
  }, []);
  const [missionRef, missionVis] = useReveal();
  const [valuesRef, valuesVis] = useReveal();
  const [teamRef, teamVis] = useReveal();
  const [statsRef, statsVis] = useReveal();
  const [servicesRef, servicesVis] = useReveal();
  const [ctaRef, ctaVis] = useReveal();

  const values = [
    { title: 'Customer First', desc: 'Every product and decision we make starts with one question: does this help our customers grow faster?' },
    { title: 'Simplicity', desc: 'We believe powerful tools should be simple. No steep learning curves — just results from day one.' },
    { title: 'Transparency', desc: 'We are honest about what our tools do, what they cost, and how they help. No hidden fees, no fine print.' },
    { title: 'Innovation', desc: 'We stay ahead of trends in AI, automation, and digital marketing to give our customers a competitive edge.' },
    { title: 'Reliability', desc: '99%+ uptime, fast response times, and 24/7 support — because your business never stops.' },
    { title: 'Growth Mindset', desc: 'We grow with our customers. As your business scales, our tools scale with you.' },
  ];

  const services = [
    { title: 'MS Chat – Chatbot + CRM', desc: 'Capture leads from your website 24/7 using a smart chatbot and manage them inside a powerful CRM dashboard.', link: 'https://mschat.msdiscoveries.com/' },
    { title: 'Email Campaign', desc: 'Send bulk email campaigns with high deliverability rates. Segment your audience, automate sequences, and track results.', link: 'https://mark.msdiscoveries.com/' },
    { title: 'Email Verifier', desc: 'Clean your mailing list by verifying emails in bulk. Reduce bounce rates and protect your sender reputation.', link: 'https://emailverifier.msdiscoveries.com/login' },
    { title: 'SEO & AEO Services', desc: 'Rank higher on Google and get discovered by AI search tools like Perplexity and ChatGPT with our SEO & Answer Engine Optimization services.', link: '/contact' },
    { title: 'Web Development', desc: 'Custom-built websites and web applications designed to convert visitors into paying customers.', link: '/contact' },
    { title: 'iOS & Android Apps', desc: 'Native and cross-platform mobile apps that extend your business reach to every smartphone.', link: '/contact' },
    { title: 'Data Services', desc: 'High-quality, targeted data sets for businesses that need accurate lead lists and market intelligence.', link: '/contact' },
  ];

  const team = [
    { name: 'MS Discoveries Team', role: 'Product & Engineering', desc: 'Builders of smart tools that make communication effortless for growing businesses.' },
    { name: 'Marketing Division', role: 'SEO, AEO & Growth', desc: 'Driving organic growth and AI search visibility for clients across industries.' },
    { name: 'Support Division', role: 'Customer Success', desc: 'Helping businesses get the most from every MS Discoveries product, 24/7.' },
  ];

  return (
    <div className="about-page">
      {/* ── HERO ── */}
      <section className="about-hero">
        <div className="about-hero__glow" />
        <div className="container about-hero__inner">
          <div className="section-tag animate-fadeInUp">About MS Discoveries</div>
          <h1 className="about-hero__title animate-fadeInUp delay-2">
            We Help Businesses <span className="highlight">Grow Faster</span><br />
            With Smart Digital Tools
          </h1>
          <p className="about-hero__desc animate-fadeInUp delay-3">
            MS Discoveries is an India-based technology company building affordable, powerful tools for businesses that want to automate communication, generate more leads, and grow digitally. From chatbots and email marketing to SEO, web development, and mobile apps — we are your all-in-one growth partner.
          </p>
          <div className="about-hero__actions animate-fadeInUp delay-4">
            <Link to="/contact" className="btn-primary"><span>Work With Us</span></Link>
            <a href="https://mschat.msdiscoveries.com/" target="_blank" rel="noreferrer" className="btn-outline">Explore Products</a>
          </div>
        </div>
      </section>

      {/* ── STATS ── */}
      <section className="about-stats" ref={statsRef}>
        <div className="container">
          <div className="about-stats__grid">
            {[
              { val: '500+', label: 'Businesses Served' },
              { val: '7', label: 'Products & Services' },
              { val: '1M+', label: 'Messages Delivered' },
              { val: '99%', label: 'Uptime Guaranteed' },
              { val: '24/7', label: 'Customer Support' },
            ].map((s, i) => (
              <div key={i} className={`about-stat ${statsVis ? 'revealed' : ''}`} style={{ animationDelay: `${i * 0.12}s` }}>
                <div className="about-stat__val">{s.val}</div>
                <div className="about-stat__label">{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── MISSION / VISION / STORY ── */}
      <section className="about-mission" ref={missionRef}>
        <div className="container">
          <div className={`about-mission__header ${missionVis ? 'revealed' : ''}`}>
            <div className="section-tag">Who We Are</div>
            <h2 className="section-title">Our Mission, Vision <span className="highlight">&amp; Story</span></h2>
          </div>
          <div className="about-mission__grid">
            {[
              {
                label: 'Our Mission',
                title: 'Making Growth Accessible',
                desc: 'To help businesses of all sizes grow faster by providing smart, affordable automation tools that improve communication, increase lead conversion, and reduce manual effort. We believe every business deserves access to enterprise-grade technology at a startup-friendly price.',
              },
              {
                label: 'Our Vision',
                title: 'The Most Trusted Growth Platform',
                desc: 'To become the most trusted all-in-one business growth platform in India and Southeast Asia — where businesses can chat, email, verify, rank, and build — all from one place. We envision a future where digital growth is simple, accessible, and results-driven.',
              },
              {
                label: 'Our Story',
                title: 'Born from a Real Problem',
                desc: 'MS Discoveries was born from a simple observation: most businesses lose leads because they cannot respond fast enough or follow up consistently. We built MS Chat to fix that. Then our customers asked for email marketing. Then verification. Then SEO. Today, MS Discoveries is a full-stack digital growth company serving 500+ businesses across India.',
              },
            ].map((item, i) => (
              <div key={i} className={`mission-card ${missionVis ? 'revealed' : ''}`} style={{ animationDelay: `${i * 0.15}s` }}>
                <div className="mission-card__label">{item.label}</div>
                <h3>{item.title}</h3>
                <p>{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── VALUES ── */}
      <section className="about-values" ref={valuesRef}>
        <div className="container">
          <div className={`about-values__header ${valuesVis ? 'revealed' : ''}`}>
            <div className="section-tag">Our Core Values</div>
            <h2 className="section-title">What We <span className="highlight">Stand For</span></h2>
            <p className="section-desc">These principles guide every product we build and every service we deliver.</p>
          </div>
          <div className="about-values__grid">
            {values.map((v, i) => (
              <div key={i} className={`value-card ${valuesVis ? 'revealed' : ''}`} style={{ animationDelay: `${i * 0.1}s` }}>
                <div className="value-card__num">0{i + 1}</div>
                <h4>{v.title}</h4>
                <p>{v.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── SERVICES ── */}
      <section className="about-services" ref={servicesRef}>
        <div className="container">
          <div className={`about-services__header ${servicesVis ? 'revealed' : ''}`}>
            <div className="section-tag">What We Offer</div>
            <h2 className="section-title">Our Products <span className="highlight">&amp; Services</span></h2>
            <p className="section-desc">Everything your business needs to generate leads, communicate better, and grow online.</p>
          </div>
          <div className="about-services__grid">
            {services.map((s, i) => (
              <div key={i} className={`about-service-card ${servicesVis ? 'revealed' : ''}`} style={{ animationDelay: `${i * 0.08}s` }}>
                <h4>{s.title}</h4>
                <p>{s.desc}</p>
                {s.link.startsWith('http') ? (
                  <a href={s.link} target="_blank" rel="noreferrer" className="about-service-card__link">Learn more →</a>
                ) : (
                  <Link to={s.link} className="about-service-card__link">Learn more →</Link>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── TEAM ── */}
      <section className="about-team" ref={teamRef}>
        <div className="container">
          <div className={`about-team__header ${teamVis ? 'revealed' : ''}`}>
            <div className="section-tag">Our Team</div>
            <h2 className="section-title">The People Behind <span className="highlight">MS Discoveries</span></h2>
            <p className="section-desc">A passionate team of builders, marketers, and support specialists dedicated to your growth.</p>
          </div>
          <div className="about-team__grid">
            {team.map((t, i) => (
              <div key={i} className={`team-card ${teamVis ? 'revealed' : ''}`} style={{ animationDelay: `${i * 0.15}s` }}>
                <div className="team-card__avatar">
                  {t.name.charAt(0)}
                </div>
                <div className="team-card__role">{t.role}</div>
                <h4>{t.name}</h4>
                <p>{t.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── CTA ── */}
      <section className="about-cta" ref={ctaRef}>
        <div className="container">
          <div className={`about-cta__inner ${ctaVis ? 'revealed' : ''}`}>
            <div className="about-cta__glow" />
            <div className="section-tag" style={{ justifyContent: 'center' }}>Ready to Grow?</div>
            <h2 className="section-title" style={{ textAlign: 'center' }}>
              Start Growing Your Business<br /><span className="highlight">With MS Discoveries Today</span>
            </h2>
            <p className="section-desc" style={{ textAlign: 'center', margin: '16px auto 36px' }}>
              Join 500+ businesses across India that trust MS Discoveries for their digital growth.
            </p>
            <div className="about-cta__btns">
              <Link to="/contact" className="btn-primary"><span>Get Started Free</span></Link>
              <a href="https://mschat.msdiscoveries.com/" target="_blank" rel="noreferrer" className="btn-outline">Try MS Chat</a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
